import { UserRoles } from "./constants";

/**
 * The viewer's role, read from the access token.
 *
 * The role lives in the token payload as `UserRoleID`, and nothing else
 * returns it: `getUserAccountDetails` carries subscription data only. Reading
 * it here rather than from the profile in Redux is both correct and faster,
 * for the same reason `permissionsFromToken` reads permissions off the token
 * (see audienceLayerEntitlement): the token is in localStorage the moment
 * somebody is signed in, where the profile fetch resolves later and would
 * briefly hide a control from the person it belongs to.
 *
 * Read, not verified. Nothing decided here is a security boundary: the API
 * verifies this same token's signature and runs the real check on every
 * request. A forged token buys somebody a button that then fails upstream.
 *
 * In src/utils because both halves of the app need it, which is this repo's
 * rule for shared logic: the v2 header decides admin nav from it, and the v1
 * Watch page decides who may pair a phone.
 */
export function roleIdFromToken(token: string | null | undefined): number | null {
  if (typeof token !== "string" || token.length === 0) return null;

  const payload = token.split(".")[1];
  if (payload === undefined || payload.length === 0) return null;

  try {
    // base64url, which is what a JWT uses: - and _ stand in for + and /, and
    // the padding is dropped. atob accepts neither.
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    // atob yields one byte per character, so a name outside Latin-1 anywhere in
    // the payload would otherwise make JSON.parse throw and lose the role with
    // it. Round-tripped through percent-encoding to get the UTF-8 back.
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const claims = JSON.parse(json) as { UserRoleID?: unknown };
    return typeof claims.UserRoleID === "number" ? claims.UserRoleID : null;
  } catch {
    // Unreadable reads as "no role", which every caller treats as the least
    // privileged answer rather than throwing inside a render.
    return null;
  }
}

/**
 * The three roles that run the platform, which is what the app has always
 * meant by "admin": the same trio the v1 header and MainContainer check before
 * offering the admin console.
 *
 * Distinct from Lens's INSIDER_ROLE_IDS, which is a different question (who
 * gets insider content) and a different set (it includes FREE_WATCH_D and
 * excludes CUSTOMER_ADMIN). Do not merge them.
 */
const ADMIN_ROLE_IDS: ReadonlySet<number> = new Set([
  UserRoles.HTP_ADMIN,
  UserRoles.LEAGUE_ADMIN,
  UserRoles.CUSTOMER_ADMIN,
]);

export function isAdminRole(roleId: number | undefined | null): boolean {
  return roleId != null && ADMIN_ROLE_IDS.has(roleId);
}

/** The two above in one read, for a caller that holds a token and not a role. */
export function isAdminToken(token: string | null | undefined): boolean {
  return isAdminRole(roleIdFromToken(token));
}
